import React, { forwardRef, useId, useState } from "react";

import renderIcon from "./renderIcon";
import { pressPoint } from "./glass";
import { cornerClass, joinedCorners, mergeRefs, useGridColumns } from "./joined";
import TagUI from "./TagUI";

const cx = (...a) => a.filter(Boolean).join(" ");

// inelul unei pastile de culoare: culoarea ei; la un gradient (perechile în
// diagonală), prima culoare din el — color-mix nu primește un gradient
function ringOf(color) {
   if (typeof color !== "string") return undefined;
   if (!color.includes("gradient(")) return color;
   const m = color.match(/var\(--[\w-]+\)|#[0-9a-f]{3,8}\b|(?:rgb|hsl)a?\([^)]*\)/i);
   return m ? m[0] : undefined;
}

/**
 * ChoiceUI — singura alegere dintre opțiuni a aplicației. Înlocuiește alegerea lipită
 * (SegmentedToggle, studentAddReservation__radio-wrapper…), filele din popup-uri
 * (staffChatPopup__tab, instrStudentsPopup__tab), filele profilului (ProfileTabs), grila
 * de plăcuțe (instructorProfileUI__typeGrid, groupEditPopupUI__sectorGrid, zilele, orele),
 * pastilele de culoare (createReservationPopup__color-swatch…) și rândul de chip-uri
 * (auditLog__chips). Stilurile: scss/UI/_choiceUI.scss.
 *
 * options: [{ value, label, icon?, count?, disabled?, dot?, color?, sublabel?, title? }]
 *    icon: nume UIIcon / svg / element · count: numărul de după text (la filele cu
 *    iconiță: în colțul iconiței) · disabled: doar opțiunea aceea · dot: punctul
 *    „așteaptă răspuns” (un text în loc de true devine eticheta lui) · color: fundalul
 *    pastilei (look="swatches"; merge și un gradient) · sublabel: rândul mic de sub text;
 *    la culori, numele afișat sub grilă (ca createReservationPopup__color-hint)
 * value: valoarea aleasă; cu `multiple`, un array · onChange(valoare | array)
 * multiple: mai multe alese (bife) în loc de una (radio-uri)
 * name: numele radio-urilor (implicit unul generat) · aria-label: numele grupului
 * look: segmented (implicit: alegerea lipită, 2–6 opțiuni pe un card black-p) |
 *       tabs (filele din popup-uri: black-p pe panoul black-s; cu iconițe = filele
 *       profilului, iconița deasupra și textul mic) |
 *       grid (plăcuțele lipite la 3px, colțurile grilei rotunjite; `columns`) |
 *       swatches (pastilele de culoare: `columns`, implicit 10) |
 *       chips (chip-urile de filtru: TagUI cu onClick / selected, în TagUI.Group)
 * size: md (implicit) | sm (filele și alegerea mai joase; grila mai mică, ca Status) |
 *       lg (doar la chips: fila-pastilă de 10×16) — la chips e mărimea TagUI
 * tone: fundalul opțiunilor nealese — implicit black-s (alegerea stă pe un card black-p) |
 *       page = black-p, pentru o alegere pusă direct pe panoul black-s (orele din
 *       Programare elev; filele, look="tabs", sunt deja așa). La chips: tonul TagUI.
 * columns: câte coloane (grid: implicit câte opțiuni sunt, cel mult 4, iar pe telefon 2,
 *          ca instructorProfileUI__typeGrid; swatches: 10)
 * disabled: toată alegerea oprită
 * Tastatura: radio-uri reale, ascunse doar vizual — Tab intră în grup, săgețile schimbă
 * alegerea; cu `multiple` sunt bife: săgețile mută între ele (în grilă sus / jos = un
 * rând), Spațiu bifează. Inelul de focus stă pe opțiune.
 */
const ChoiceUI = forwardRef(function ChoiceUI(
   {
      options = [],
      value,
      onChange,
      multiple = false,
      name,
      look = "segmented",
      size = "md",
      tone,
      columns,
      disabled = false,
      className = "",
      style,
      "aria-label": ariaLabel,
      ...rest
   },
   ref,
) {
   const autoId = useId();
   const [hovered, setHovered] = useState(null);
   // grila: coloanele de fapt (și pe telefon), pentru colțurile plăcuțelor de la margine
   const [measured, gridRef] = useGridColumns(columns || 4);
   const list = Array.isArray(options) ? options.filter(Boolean) : [];
   const picked = multiple && Array.isArray(value) ? value : [];
   const isOn = (v) =>
      multiple ? picked.some((x) => String(x) === String(v)) : value !== undefined && value !== null && String(value) === String(v);
   const pick = (v) => {
      if (!multiple) return onChange?.(v);
      return onChange?.(isOn(v) ? picked.filter((x) => String(x) !== String(v)) : [...picked, v]);
   };

   // ── chip-urile de filtru: TagUI apăsabile ──
   if (look === "chips") {
      return (
         <TagUI.Group ref={ref} className={className} style={style} aria-label={ariaLabel || name || "Opțiuni"} {...rest}>
            {list.map((o) => (
               <TagUI
                  key={String(o.value)}
                  size={size}
                  tone={tone}
                  icon={o.icon}
                  count={o.count}
                  title={o.title}
                  selected={isOn(o.value)}
                  disabled={disabled || Boolean(o.disabled)}
                  onClick={() => pick(o.value)}
               >
                  {o.label}
               </TagUI>
            ))}
         </TagUI.Group>
      );
   }

   const groupName = name || `choiceUI${autoId}`;
   const swatches = look === "swatches";
   const gridLike = look === "grid" || swatches;
   const stacked = look === "tabs" && list.some((o) => o.icon);
   const cols = columns || (swatches ? 10 : look === "grid" ? Math.max(1, Math.min(list.length, 4)) : undefined);
   // grila: plăcuțele de la margine își rotunjesc singure colțurile de afară, după
   // coloanele de fapt (și pe telefon); grila nu taie nimic
   const corners = look === "grid" ? joinedCorners(list.map(() => 1), measured) : null;
   // grila fără columns: pe telefon 2 coloane (ca instructorProfileUI__typeGrid)
   const mobileCols = look === "grid" && !columns ? Math.min(cols, 2) : undefined;

   // numele culorii sub grilă: cea de sub mouse / din focus, altfel cea aleasă
   const showHint = swatches && list.some((o) => o.sublabel);
   const current = list.find((o) => isOn(o.value));
   const hint = hovered ?? (current ? current.sublabel || current.label : "");

   // cu bife săgețile nu merg singure: le mutăm noi (în grilă, sus / jos = un rând;
   // peste opțiunile oprite se sare)
   const onKeyDown = (e) => {
      if (!multiple) return;
      const inputs = Array.from(e.currentTarget.querySelectorAll("input"));
      const i = inputs.indexOf(document.activeElement);
      if (i < 0) return;
      const last = inputs.length - 1;
      const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: gridLike ? cols : 1, ArrowUp: gridLike ? -cols : -1, Home: -last, End: last }[e.key];
      if (!step) return;
      e.preventDefault();
      const dir = step > 0 ? 1 : -1;
      let j = Math.min(last, Math.max(0, i + step));
      while (inputs[j].disabled && j + dir >= 0 && j + dir <= last) j += dir;
      if (!inputs[j].disabled) inputs[j].focus();
   };

   return (
      <div
         ref={look === "grid" ? mergeRefs(ref, gridRef) : ref}
         role={multiple ? "group" : "radiogroup"}
         aria-label={ariaLabel || name || "Opțiuni"}
         className={cx(
            "choiceUI",
            `choiceUI--${look}`,
            size !== "md" && `choiceUI--${size}`,
            stacked && "choiceUI--stacked",
            tone === "page" && !swatches && "choiceUI--page",
            disabled && "is-disabled",
            className,
         )}
         style={cols ? { "--choiceUI-cols": cols, "--choiceUI-cols-mobile": mobileCols, ...style } : style}
         onKeyDown={multiple ? onKeyDown : undefined}
         {...rest}
      >
         {list.map((o, index) => {
            const on = isOn(o.value);
            const off = disabled || Boolean(o.disabled);
            const dotLabel = typeof o.dot === "string" ? o.dot : undefined;
            const hasCount = o.count !== undefined && o.count !== null && o.count !== false;
            const tip = swatches ? o.sublabel || o.label : undefined;
            const enter = showHint ? () => setHovered(tip) : undefined;
            const leave = showHint ? () => setHovered(null) : undefined;
            return (
               <label
                  key={String(o.value)}
                  className={cx("choiceUI__option", on && "is-selected", off && "is-disabled", corners && cornerClass(corners[index]))}
                  style={swatches && o.color ? { "--choiceUI-sw": o.color, "--choiceUI-ring": ringOf(o.color) } : undefined}
                  title={o.title ?? tip}
                  onMouseEnter={enter}
                  onMouseLeave={leave}
                  onPointerDown={off ? undefined : pressPoint}
               >
                  <input
                     type={multiple ? "checkbox" : "radio"}
                     name={groupName}
                     value={String(o.value)}
                     checked={on}
                     disabled={off}
                     onChange={() => pick(o.value)}
                     aria-label={
                        swatches
                           ? o.sublabel && o.sublabel !== o.label
                              ? `${o.label}: ${o.sublabel}`
                              : o.label
                           : stacked && hasCount
                             ? `${o.label}, ${o.count}` // numărul din colț e ascuns cititorului
                             : undefined
                     }
                     onFocus={enter}
                     onBlur={leave}
                  />
                  {!swatches && (
                     <>
                        {stacked ? (
                           <span className="choiceUI__iconWrap">
                              {renderIcon(o.icon, "choiceUI__icon")}
                              {hasCount && <TagUI corner tone="danger" count={o.count} />}
                           </span>
                        ) : (
                           renderIcon(o.icon, "choiceUI__icon")
                        )}
                        <span className="choiceUI__label">
                           <span className="choiceUI__text">{o.label}</span>
                           {o.sublabel && <span className="choiceUI__sublabel">{o.sublabel}</span>}
                        </span>
                        {o.dot && (
                           <span
                              className="choiceUI__dot"
                              role={dotLabel ? "img" : undefined}
                              aria-label={dotLabel}
                              aria-hidden={dotLabel ? undefined : "true"}
                              title={dotLabel}
                           />
                        )}
                        {hasCount && !stacked && <span className="choiceUI__count">{o.count}</span>}
                     </>
                  )}
               </label>
            );
         })}
         {showHint && (
            // numele e deja în aria-label-ul fiecărei pastile
            <p className="choiceUI__hint" aria-hidden="true">
               {hint}
            </p>
         )}
      </div>
   );
});

export default ChoiceUI;
